import React, { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { playerApplicationsAPI } from '../services/api';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { getErrorMessage } from '../utils/errorMessage';
import { clubLogoSrc, handleClubLogoError } from '../utils/clubLogo';
import type { SubmitPlayerApplicationRequest } from '../types/playerApplication';

const emptyForm: SubmitPlayerApplicationRequest = {
  playerName: '',
  birth: '',
  gender: '',
  placeOfBirth: '',
  address: '',
  playerPhone: '',
  parentName: '',
  parentEmail: '',
  parentPhone: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  medicalNotes: '',
  previousClub: '',
  note: '',
  termsAccepted: false,
  website: '',
};

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm';

/**
 * Nyilvános jelentkezési űrlap új játékosoknak - bejelentkezés nélkül, a klub
 * weboldaláról vagy QR-kódról. A jelentkezés a stábhoz kerül elbírálásra.
 */
const Apply: React.FC = () => {
  usePageTitle('Jelentkezés');
  const { settings, loading } = useClubSettings();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const clubName = settings?.clubName || 'a klub';

  const set =
    (key: keyof SubmitPlayerApplicationRequest) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const trimmed = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === 'string' ? v.trim() || undefined : v])
      ) as unknown as SubmitPlayerApplicationRequest;
      await playerApplicationsAPI.submit(trimmed);
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (err: any) {
      setError(
        err?.response?.status === 429
          ? 'Túl sok jelentkezés érkezett erről a hálózatról. Kérjük, próbáld újra pár perc múlva.'
          : getErrorMessage(err, 'A jelentkezést nem sikerült elküldeni.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <div className="text-center">
      <img
        src={clubLogoSrc(settings?.crestUrl)}
        alt=""
        className="mx-auto mb-4 h-20 w-20 object-contain"
        onError={handleClubLogoError}
      />
      <h1 className="text-3xl font-extrabold text-gray-900">{submitted ? 'Köszönjük!' : 'Jelentkezés'}</h1>
      {!submitted && <p className="mt-2 text-sm text-gray-600">{settings?.clubName || 'A klub'} - új játékosok jelentkezése</p>}
    </div>
  );

  if (loading) {
    return <div className="min-h-screen bg-gray-50" />;
  }

  if (!settings?.applicationsEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
        <div className="w-full max-w-md space-y-6">
          {header}
          <p className="text-center text-sm text-gray-600">
            A jelentkezés jelenleg nem elérhető. Kérjük, keresd közvetlenül {clubName} edzőit vagy vezetőségét.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 sm:px-6">
      <div className="mx-auto w-full max-w-2xl space-y-8">
        {header}

        {submitted ? (
          <div className="rounded-lg bg-green-50 p-4">
            <div className="flex gap-3">
              <CheckCircleIcon className="h-6 w-6 shrink-0 text-green-600" />
              <div className="text-sm text-green-800">
                <p className="font-medium">Megkaptuk a jelentkezést.</p>
                <p className="mt-1">
                  Az edzők hamarosan átnézik, és a megadott email címre írunk. Elfogadás után meghívót is kapsz a klub
                  alkalmazásába, ahol az edzéseket és a fontos tudnivalókat követheted.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {settings.applicationIntro && (
              <div className="whitespace-pre-line rounded-lg bg-white p-4 text-sm text-gray-700 shadow-sm">
                {settings.applicationIntro}
              </div>
            )}

            {error && <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>}

            {/* Csapda a robotoknak: ember nem látja, nem tölti ki */}
            <div aria-hidden="true" className="absolute -left-[10000px] h-0 w-0 overflow-hidden">
              <label>
                Weboldal
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
              </label>
            </div>

            <section className="space-y-4 rounded-lg bg-white p-4 shadow-sm sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900">A játékos</h2>
              <label className="block text-sm font-medium text-gray-700">
                Teljes név *
                <input type="text" required minLength={3} maxLength={100} value={form.playerName} onChange={set('playerName')} className={inputClass} />
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  Születési dátum *
                  <input type="date" required value={form.birth} onChange={set('birth')} className={inputClass} />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Nem *
                  <select required value={form.gender} onChange={set('gender')} className={inputClass}>
                    <option value="">Válassz</option>
                    <option value="Férfi">Fiú</option>
                    <option value="Nő">Lány</option>
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Születési hely
                  <input type="text" maxLength={100} value={form.placeOfBirth} onChange={set('placeOfBirth')} className={inputClass} />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  A játékos telefonszáma
                  <input type="tel" maxLength={30} value={form.playerPhone} onChange={set('playerPhone')} className={inputClass} placeholder="ha van saját" />
                </label>
              </div>
              <label className="block text-sm font-medium text-gray-700">
                Lakcím
                <input type="text" maxLength={200} value={form.address} onChange={set('address')} className={inputClass} autoComplete="street-address" />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Korábbi egyesület
                <input type="text" maxLength={200} value={form.previousClub} onChange={set('previousClub')} className={inputClass} placeholder="ha volt" />
              </label>
            </section>

            <section className="space-y-4 rounded-lg bg-white p-4 shadow-sm sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900">Szülő / gondviselő</h2>
              <label className="block text-sm font-medium text-gray-700">
                Név *
                <input type="text" required maxLength={100} value={form.parentName} onChange={set('parentName')} className={inputClass} autoComplete="name" />
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email cím *
                  <input type="email" required maxLength={256} value={form.parentEmail} onChange={set('parentEmail')} className={inputClass} autoComplete="email" />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Telefonszám *
                  <input type="tel" required maxLength={30} value={form.parentPhone} onChange={set('parentPhone')} className={inputClass} autoComplete="tel" />
                </label>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  Vészhelyzeti kontakt
                  <input type="text" maxLength={100} value={form.emergencyContactName} onChange={set('emergencyContactName')} className={inputClass} placeholder="ha a szülők nem érhetők el" />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Vészhelyzeti telefon
                  <input type="tel" maxLength={30} value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} className={inputClass} />
                </label>
              </div>
            </section>

            <section className="space-y-4 rounded-lg bg-white p-4 shadow-sm sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900">Egyéb</h2>
              <label className="block text-sm font-medium text-gray-700">
                Egészségügyi tudnivalók
                <textarea rows={2} maxLength={1000} value={form.medicalNotes} onChange={set('medicalNotes')} className={inputClass} placeholder="Allergia, asztma, rendszeres gyógyszer" />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Megjegyzés
                <textarea rows={3} maxLength={1000} value={form.note} onChange={set('note')} className={inputClass} placeholder="Bármi, amit tudnunk érdemes" />
              </label>
            </section>

            {settings.applicationTerms && (
              <section className="space-y-3 rounded-lg bg-white p-4 shadow-sm sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900">Feltételek és adatkezelés</h2>
                <div className="max-h-48 overflow-y-auto whitespace-pre-line rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  {settings.applicationTerms}
                </div>
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    required
                    checked={form.termsAccepted}
                    onChange={(e) => setForm((prev) => ({ ...prev, termsAccepted: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Elolvastam és elfogadom a fenti feltételeket, és hozzájárulok a megadott adatok kezeléséhez. *</span>
                </label>
              </section>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Küldés...' : 'Jelentkezés elküldése'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Apply;
